import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { catchError, combineLatest, concatMap, map, Observable, of, switchMap, throwError } from 'rxjs';
import { BACKEND_URLS } from 'app/app.config';
import { CookieService } from 'ngx-cookie-service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private backendUrls = inject(BACKEND_URLS);
    private _cookieService = inject(CookieService);

    authenticated: boolean = false;

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        sessionStorage.setItem('__iol', token);
    }

    get accessToken(): string {
        return sessionStorage.getItem('__iol') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }
        return this._httpClient.post(`${this.backendUrls.backendApiUrl}/login`, credentials).pipe(
            map((result) => result['data']),
            switchMap((response: any) => {
                // Store the access token in the local storage
                this.accessToken = response.access_token;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable <boolean> {

        // Sign in using the token
        return this._httpClient
            .post(`${this.backendUrls.backendApiUrl}/login/new_access_token`, {})
            .pipe(
                map((response: any) => {


                    // Replace the access token with the new one if it's available on
                    // the response object.
                    //
                    // This is an added optional step for better security. Once you sign
                    // in using the token, you should generate a new one on the server
                    // side and attach it to the response object. Then the following
                    // piece of code can replace the token with the refreshed one.
                    if (response.data.access_token) {
                        console.log(response.data.access_token)

                        this.accessToken = response.data.access_token;
                    }

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.data.user;
                    console.log(this._userService.user)
                    // Return true
                    return true;
                }),
                catchError((err) => {
                    const message = 'Accesso al portale plone fallito';

                    console.log("XXXXXXXXXXXXXXXXXXXXXXXXX")


                    return of(false);
                })
            );


    }

    /**
     * Sign in using the access token
     */
    signInUsingSPID(): Observable<any> {
        // Sign in using the token

        return this._httpClient
            .post(`${this.backendUrls.backendApiUrl}/login/spid-user`,{})
            .pipe(
                catchError((error) =>
                {
                    console.log(error)
                    // Catch "401 Unauthorized" responses
                    if ( error instanceof HttpErrorResponse && error.status === 401 )
                    {
                        // Sign out
                        this.signOut();
                        // Reload the app
                        location.reload();
                    }
                    //return of(false)
                    return throwError(error);
                }),
                map((result) => result['data']),
                switchMap((response: any) => {
                    console.log(response)
                    // Replace the access token with the new one if it's available on
                    // the response object.
                    //
                    // This is an added optional step for better security. Once you sign
                    // in using the token, you should generate a new one on the server
                    // side and attach it to the response object. Then the following
                    // piece of code can replace the token with the refreshed one.
                    if (response.access_token) {


                        this.accessToken = response.access_token;
                    }

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;

                    // Return true
                    //return of(false)
                    return of(true);
                })
            );
    }


    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        sessionStorage.removeItem('__iol');
        // Set the authenticated flag to false
        this._authenticated = false;

        return combineLatest([this._httpClient.post(`${this.backendUrls.backendApiUrl}/login/sign_out`,{}),this._userService._user])
        //this._httpClient.post(`${this.backendUrls.backendApiUrl}/login/sign_out`,{}).subscribe()
        //window.location.href='https://www.comune.sanlazzaro.bo.it/'


        //return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

   /**
     * Check the authentication status
     */
   check(): Observable<boolean>
   {


       console.log(this)
       // Check if the user is logged in
       if ( this._authenticated )
       {
           return of(true);
       }

       // Check the access token availability
       if ( !this.accessToken )
       {
            return of(false);

       }


       // Check the access token expire date
       if ( AuthUtils.isTokenExpired(this.accessToken) )
       {
           //return this.signInUsingToken();
           return of(false);
       }

       // If the access token exists, and it didn't expire, sign in using it
       //return this.signInUsingToken();

       return of(false)
   }



    /**
     * Check the authentication status
     */
    checkplone(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return this.signInUsingPlone();
            return of(true);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return this.signInUsingPlone();
            return of(true);
        }

        // If the access token exists, and it didn't expire, sign in using it
        //return this.signInUsingToken();
        return this.signInUsingPlone();
    }

    signInUsingPlone(): Observable<boolean> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }
        return this._httpClient
            .post(`${this.backendUrls.backendApiUrl}/plone_login/access-token`, null)
            .pipe(
                switchMap((response: any) => {
                    if (!response) {
                        return of(false);
                    }

                    if (response.access_token) {
                        // Store the access token in the local storage
                        this.accessToken = response.access_token;

                        // Set the authenticated flag to true
                        this._authenticated = true;

                        this._userService.user = response.user;
                        console.log(response);
                        // Return a new observable with the response
                        return of(true);
                    } else {
                        window.location.href = response.redirect;
                        return of(false);
                    }
                }),
                catchError((err) => {
                    const message = 'Accesso al portale plone fallito';
                    return of(false);
                })
            );
    }
}
